/**
 * Working-day checks for a deal's recurring monthly deadline day.
 *
 * A deadline day is a plain day-of-month (1-28), so whether it lands on a working
 * day depends on which month you evaluate it against. The reference month is the
 * deal's start period, falling back to the month after "now" once the start period
 * is in the past — see `resolveDeadlineReferenceMonth`.
 *
 * Holidays are deliberately limited to New Year's Day, Christmas Day and Boxing
 * Day. There is no substitute-day logic: a holiday that falls on a weekend simply
 * reports the holiday reason.
 */

export type TNonWorkingDayReason = "saturday" | "sunday" | "new-year" | "christmas" | "boxing-day";

export type TDeadlineDayWarning = {
    /** The concrete date that was evaluated. */
    date: Date;
    reason: TNonWorkingDayReason;
    /** Nearest working day-of-month to suggest, or null when none fits the month. */
    suggestedDay: number | null;
    /** Ready-to-render sentence. */
    message: string;
};

export type TDeadlineReferenceMonth = { year: number; month: number };

/** Highest day a deadline may be set to — mirrors the 1-28 range the API enforces. */
const MAX_DEADLINE_DAY = 28;

const REASON_LABELS: Record<TNonWorkingDayReason, string> = {
    saturday: "a Saturday",
    sunday: "a Sunday",
    "new-year": "New Year's Day",
    christmas: "Christmas Day",
    "boxing-day": "Boxing Day",
};

const MONTH_NAMES = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
];

export function ordinal(day: number): string {
    // 11th/12th/13th are the exceptions to the 1st/2nd/3rd pattern.
    const suffix =
        day % 100 >= 11 && day % 100 <= 13
            ? "th"
            : day % 10 === 1
              ? "st"
              : day % 10 === 2
                ? "nd"
                : day % 10 === 3
                  ? "rd"
                  : "th";
    return `${day}${suffix}`;
}

/** Returns null when the date is a working day. */
export function getNonWorkingDayReason(date: Date): TNonWorkingDayReason | null {
    const month = date.getMonth() + 1;
    const day = date.getDate();

    // Holidays win over the weekday so the message names the actual reason.
    if (month === 1 && day === 1) return "new-year";
    if (month === 12 && day === 25) return "christmas";
    if (month === 12 && day === 26) return "boxing-day";

    const weekday = date.getDay();
    if (weekday === 6) return "saturday";
    if (weekday === 0) return "sunday";

    return null;
}

/**
 * Resolve which month a recurring deadline day should be evaluated against.
 *
 * `startPeriod` accepts "YYYY-MM-DD" or "YYYY-MM". The start period is used when it
 * is the current month or later; once it is in the past (or missing/unparseable) the
 * next deadline the user will actually hit is in the month after `now`.
 */
export function resolveDeadlineReferenceMonth(
    startPeriod: string | null,
    now: Date,
): TDeadlineReferenceMonth {
    const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    const fallback = { year: nextMonth.getFullYear(), month: nextMonth.getMonth() + 1 };

    const match = startPeriod?.match(/^(\d{4})-(\d{2})/);
    if (!match) return fallback;

    const year = Number(match[1]);
    const month = Number(match[2]);
    if (month < 1 || month > 12) return fallback;

    const isPast =
        year < now.getFullYear() || (year === now.getFullYear() && month < now.getMonth() + 1);

    return isPast ? fallback : { year, month };
}

/**
 * Walk outwards from `day` for the nearest working day that is still a valid deadline
 * day in the same month: forwards first (the user's next chance to submit), then
 * backwards if the month runs out.
 */
function findSuggestedDay(day: number, reference: TDeadlineReferenceMonth): number | null {
    for (let candidate = day + 1; candidate <= MAX_DEADLINE_DAY; candidate++) {
        const date = new Date(reference.year, reference.month - 1, candidate);
        if (getNonWorkingDayReason(date) === null) return candidate;
    }
    for (let candidate = day - 1; candidate >= 1; candidate--) {
        const date = new Date(reference.year, reference.month - 1, candidate);
        if (getNonWorkingDayReason(date) === null) return candidate;
    }
    return null;
}

/**
 * Advisory check for a deadline day in a given month.
 *
 * Returns null when the day is a working day, or when it is outside the 1-28 range —
 * the range error owns those values so the two messages never stack.
 */
export function checkDeadlineDay(
    day: number,
    reference: TDeadlineReferenceMonth,
): TDeadlineDayWarning | null {
    if (!Number.isInteger(day) || day < 1 || day > MAX_DEADLINE_DAY) return null;

    const date = new Date(reference.year, reference.month - 1, day);
    const reason = getNonWorkingDayReason(date);
    if (reason === null) return null;

    const suggestedDay = findSuggestedDay(day, reference);
    const dateLabel = `${day} ${MONTH_NAMES[reference.month - 1]} ${reference.year}`;
    const message =
        suggestedDay === null
            ? `${dateLabel} is ${REASON_LABELS[reason]}.`
            : `${dateLabel} is ${REASON_LABELS[reason]}. Consider the ${ordinal(suggestedDay)} instead.`;

    return { date, reason, suggestedDay, message };
}
